import { Icon } from "@circle-react-shared/Icon";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useEffect, useState } from "react";

import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";
import { useCircleV4 } from "@/react/hooks/useCircleV4";

import { useAiSummaryContext } from "./Context";
import { TypingDots } from "./TypingDots";

interface AiSummaryHeaderProps {
  animate?: boolean;
  children: React.ReactNode;
  isHidden?: boolean;
}

export const AiSummaryHeader = ({
  children,
  animate = false,
  isHidden = false,
}: AiSummaryHeaderProps) => {
  const { isCollapsed } = useAiSummaryContext();
  const { isCircleV4 } = useCircleV4();
  const [isCompletelyHidden, setIsCompletelyHidden] = useState(isHidden);

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;
    if (isHidden) {
      timeout = setTimeout(() => {
        setIsCompletelyHidden(true);
      }, 300);
    } else {
      setIsCompletelyHidden(false);
    }
    return () => timeout && clearTimeout(timeout);
  }, [isHidden]);

  if (isCompletelyHidden) {
    return null;
  }

  if (isCircleV4) {
    return (
      <div
        className={classNames(
          "flex w-full items-center gap-1.5 transition-all duration-300",
          { "h-0 opacity-0": isHidden },
        )}
      >
        <TypingDots />
        <span
          className={classNames("text-sm font-semibold leading-tight", {
            "group-hover:bg-gradient-to-b group-hover:from-[#516DF1] group-hover:to-[#6B47D2] group-hover:bg-clip-text group-hover:text-transparent":
              isCollapsed,
            "bg-gradient-to-r from-[#3C53E7] to-[#8994DC] bg-clip-text text-transparent":
              !isCollapsed,
          })}
        >
          {children}
        </span>
      </div>
    );
  }

  return (
    <div
      className={classNames(
        "flex w-full items-center gap-1.5 transition-all duration-300",
        {
          "h-0 opacity-0": isHidden,
        },
      )}
    >
      {isCollapsed && (
        <div className="relative flex size-5 items-center justify-center">
          <Icon
            type="20-stardust"
            size={20}
            className="text-dark absolute !h-5 !w-5 opacity-100 transition-opacity duration-300 group-hover:opacity-0"
          />
          <Icon
            type="20-stardust-gradient"
            size={20}
            className="absolute !h-5 !w-5 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
          />
        </div>
      )}
      {!isCollapsed && (
        <Icon
          type="20-stardust-gradient"
          size={20}
          className={classNames("!h-5 !w-5", {
            "animate-spin-pause-reverse": animate,
          })}
        />
      )}
      <Typography.LabelSm
        weight="semibold"
        color={classNames("text-dark", {
          "group-hover:bg-gradient-to-b group-hover:from-[#516DF1] group-hover:to-[#6B47D2] group-hover:bg-clip-text group-hover:text-transparent":
            isCollapsed,
          "bg-gradient-to-r from-[#6775EE] to-[#AD89DC] bg-clip-text text-transparent":
            !isCollapsed,
        })}
        leading="leading-tight"
      >
        {children}
      </Typography.LabelSm>
    </div>
  );
};
